import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DashboardRouter from "@/pages/DashboardRouter";
import MyTasks from "@/pages/MyTasks";
import ExpenseQuickSubmit from "@/pages/ExpenseQuickSubmit";
import Notifications from "@/pages/Notifications";
import NotFound from "@/pages/NotFound";
import Receivables from "@/pages/Receivables";
import ProductRegistry from "@/pages/ProductRegistry";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Clients from "@/pages/Clients";
import Invoices from "@/pages/Invoices";
import Payables from "@/pages/Payables";
import Escalations from "@/pages/Escalations";
import FieldOps from "@/pages/FieldOps";
import Intelligence from "@/pages/Intelligence";
import Quality from "@/pages/Quality";
import Settings from "@/pages/Settings";
import Leads from "@/pages/Leads";
import Analytics from "@/pages/Analytics";
import HealthScore from "@/pages/HealthScore";
import Billing from "@/pages/Billing";
import AuditLog from "@/pages/AuditLog";
import Developer from "@/pages/Developer";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Verify from "@/pages/Verify";
import Registry from "@/pages/Registry";
import StateOfBrand from "@/pages/StateOfBrand";
import NetworkPage from "@/pages/NetworkPage";
import Products from "@/pages/Products";
import Parties from "@/pages/Parties";
import Report from "@/pages/Report";
import MapPage from "@/pages/MapPage";
import Reports from "@/pages/Reports";
import Capture from "@/pages/Capture";
import GovVerify from "@/pages/GovVerify";
import BuildStatus from "@/pages/BuildStatus";
import Contractors from "@/pages/Contractors";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import WebhookDeliveries from "@/pages/WebhookDeliveries";
import PortalCases from "@/pages/PortalCases";
import Tap2Verify from "@/pages/Tap2Verify";
import Tap2VerifyPublic from "@/pages/Tap2VerifyPublic";
import LegalTemplates from "@/pages/LegalTemplates";
import BundleReview from "@/pages/BundleReview";
import CaseNFOR from "@/pages/CaseNFOR";
import Tap2VerifyWidget from "@/pages/Tap2VerifyWidget";
import Onboarding from "@/pages/Onboarding";
import InvestigationTracker from "@/pages/InvestigationTracker";
import Approvals from "@/pages/Approvals";
import RecycleBin from "@/pages/RecycleBin";
import CustomFields from "@/pages/CustomFields";
import FeatureToggles from "@/pages/FeatureToggles";
import Profile from "@/pages/Profile";
import DepartmentManagement from "@/pages/DepartmentManagement";
import InviteUser from "@/pages/InviteUser";
import OrgBranding from "@/pages/OrgBranding";
import CaseLifecycles from "@/pages/CaseLifecycles";
import CommandCenter from "@/pages/CommandCenter";
import AuditTrailDashboard from "@/pages/AuditTrailDashboard";
import DripCampaigns from "@/pages/DripCampaigns";
import StakeholderDirectory from "@/pages/StakeholderDirectory";
import FinanceDashboard from "@/pages/FinanceDashboard";
import ChannelInbox from "@/pages/ChannelInbox";
import ChannelLeads from "@/pages/ChannelLeads";
import ChannelDashboard from "@/pages/ChannelDashboard";
import FlowBuilder from "@/pages/FlowBuilder";
import LeavePage from "@/pages/LeavePage";
import LeaveApprovals from "@/pages/LeaveApprovals";
import { SightingReports, SightingReportNew, SightingReportDetail } from "@/pages/SightingReport";
import TeamDirectory from "@/pages/TeamDirectory";
import OrgChart from "@/pages/OrgChart";
import BulkReassign from "@/pages/BulkReassign";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminPeople from "@/pages/AdminPeople";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordRecovery";
import PwaInstall from "@/components/PwaInstall";
import CommandPalette from "@/components/CommandPalette";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/forgot" element={<ForgotPassword />} />
      <Route path="/auth/reset" element={<ResetPassword />} />
      <Route path="/health-score" element={<HealthScore />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/counterfeit-registry" element={<Registry />} />
      <Route path="/state-of-brand" element={<StateOfBrand />} />
      <Route path="/report" element={<Report />} />
      <Route path="/tap2verify" element={<Tap2VerifyPublic />} />
      <Route path="/review/bundle" element={<BundleReview />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardRouter />} />
        <Route path="/my-tasks" element={<MyTasks />} />
        <Route path="/expenses/new" element={<ExpenseQuickSubmit />} />
        <Route path="/expenses" element={<ExpenseQuickSubmit />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/receivables" element={<Receivables />} />
        <Route path="/products/registry" element={<ProductRegistry />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/cases/:caseId" element={<CaseDetail />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/products" element={<Products />} />
        <Route path="/parties" element={<Parties />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/payables" element={<Payables />} />
        <Route path="/escalations" element={<Escalations />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/field-ops" element={<FieldOps />} />
        <Route path="/intelligence" element={<Intelligence />} />
        <Route path="/network" element={<NetworkPage />} />
        <Route path="/quality" element={<Quality />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/capture" element={<Capture />} />
        <Route path="/gov-verify" element={<GovVerify />} />
        <Route path="/contractors" element={<Contractors />} />
        <Route path="/whatsapp" element={<WhatsAppInbox />} />
        <Route path="/webhooks" element={<WebhookDeliveries />} />
        <Route path="/portal/cases" element={<PortalCases />} />
        <Route path="/admin/tap2verify" element={<Tap2Verify />} />
        <Route path="/admin/build-status" element={<BuildStatus />} />
        <Route path="/legal-templates" element={<LegalTemplates />} />
        <Route path="/cases/nfor" element={<CaseNFOR />} />
        <Route path="/cases/tracker" element={<InvestigationTracker />} />
        <Route path="/reports/sighting" element={<SightingReports />} />
        <Route path="/reports/sighting/new" element={<SightingReportNew />} />
        <Route path="/reports/sighting/:id" element={<SightingReportDetail />} />
        <Route path="/settings/tap2verify-widget" element={<Tap2VerifyWidget />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/settings/team" element={<TeamDirectory />} />
        <Route path="/org" element={<OrgChart />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/developer" element={<Developer />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/recycle-bin" element={<RecycleBin />} />
        <Route path="/settings/custom-fields" element={<CustomFields />} />
        <Route path="/settings/admin/feature-toggles" element={<FeatureToggles />} />
        <Route path="/settings/profile" element={<Profile />} />
        <Route path="/settings/admin/departments" element={<DepartmentManagement />} />
        <Route path="/settings/admin/invite-user" element={<InviteUser />} />
        <Route path="/settings/admin/branding" element={<OrgBranding />} />
        <Route path="/settings/admin/lifecycles" element={<CaseLifecycles />} />
        <Route path="/admin" element={<CommandCenter />} />
        <Route path="/admin/bulk-reassign" element={<BulkReassign />} />
        <Route path="/admin/onboarding" element={<AdminOnboarding />} />
        <Route path="/admin/people" element={<AdminPeople />} />
        <Route path="/admin/audit-trail" element={<AuditTrailDashboard />} />
        <Route path="/stakeholders/drip" element={<DripCampaigns />} />
        <Route path="/stakeholders" element={<StakeholderDirectory />} />
        <Route path="/finance/dashboard" element={<FinanceDashboard />} />
        <Route path="/channel/dashboard" element={<ChannelDashboard />} />
        <Route path="/channel/inbox" element={<ChannelInbox />} />
        <Route path="/channel/leads" element={<ChannelLeads />} />
        <Route path="/channel/flows" element={<FlowBuilder />} />
        <Route path="/hr/leave" element={<LeavePage />} />
        <Route path="/hr/leave/approvals" element={<LeaveApprovals />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
            <PwaInstall />
            <CommandPalette />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}
